import { useEffect } from 'react'
import type { BiomassDataRow } from '../../utils/productionUnitTable'
import { useT } from '../../i18n'

/**
 * Same-day rows drill-down popover (read-only).
 *
 * Listed in [docs/release/v2.1/findings.md](../../../docs/release/v2.1/findings.md)
 * row #4: every Sampling save inserts a new row even when several samplings
 * land on the same `(lot, calendar day)`. Until v2.2 redesigns the write
 * semantics, this popover gives operators evidence that the rows truly exist
 * by listing every sibling for the same day.
 *
 * Read-only by design for v2.1; edits/deletes continue to flow through the
 * Biomass Sampling form's per-row Delete action.
 */
export interface SameDayRowsPopoverProps {
  rows: BiomassDataRow[]
  lotLabel: string
  dateLabel: string
  onClose: () => void
}

const formatRowTime = (rawDate: string): string => {
  if (!rawDate) return ''
  const parsed = new Date(rawDate)
  if (Number.isNaN(parsed.getTime())) return rawDate
  try {
    return parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return parsed.toISOString().slice(11, 16)
  }
}

const formatHeaderDate = (rawDate: string): string => {
  if (!rawDate) return ''
  const parsed = new Date(rawDate)
  if (Number.isNaN(parsed.getTime())) return rawDate
  try {
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return parsed.toISOString().slice(0, 10)
  }
}

const sortRowsByTime = (rows: BiomassDataRow[]): BiomassDataRow[] => {
  return [...rows].sort((a, b) => {
    const aTime = Date.parse(String(a.date ?? ''))
    const bTime = Date.parse(String(b.date ?? ''))
    const aValid = Number.isFinite(aTime)
    const bValid = Number.isFinite(bTime)
    if (aValid && bValid && aTime !== bTime) return aTime - bTime
    if (aValid && !bValid) return -1
    if (!aValid && bValid) return 1
    return String(a.id ?? '').localeCompare(String(b.id ?? ''))
  })
}

const formatCellText = (value: unknown): string => {
  if (value === null || value === undefined) return '—'
  const text = String(value).trim()
  return text || '—'
}

export const sameDayRowsPopoverHelpers = {
  formatRowTime,
  formatHeaderDate,
  sortRowsByTime,
  formatCellText
}

export const SameDayRowsPopover = ({
  rows,
  lotLabel,
  dateLabel,
  onClose
}: SameDayRowsPopoverProps) => {
  const t = useT()
  const formattedDate = formatHeaderDate(dateLabel)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKey)
    return () => globalThis.removeEventListener('keydown', onKey)
  }, [onClose])

  const sorted = sortRowsByTime(rows)

  return (
    <div
      data-testid="same-day-rows-popover"
      role="dialog"
      aria-modal="true"
      aria-label={t('datatable.sameDayRows.popover.title', { lot: lotLabel, date: formattedDate })}
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#ffffff',
          borderRadius: 12,
          minWidth: 320,
          maxWidth: 640,
          width: '100%',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)'
        }}
      >
        <header
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>
              {t('datatable.sameDayRows.popover.title', { lot: lotLabel, date: formattedDate })}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#475569' }}>
              {t('datatable.sameDayRows.popover.total', { count: String(rows.length) })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('datatable.sameDayRows.popover.close')}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 20,
              color: '#475569',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ✕
          </button>
        </header>
        <div style={{ overflowY: 'auto', padding: '12px 20px 20px' }}>
          {sorted.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
              {t('datatable.sameDayRows.popover.empty')}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#475569' }}>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
                    {t('datatable.sameDayRows.popover.column.time')}
                  </th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                    {t('datatable.sameDayRows.popover.column.weight')}
                  </th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                    {t('datatable.sameDayRows.popover.column.mortality')}
                  </th>
                  <th style={{ padding: '6px 4px', borderBottom: '1px solid #e2e8f0', fontWeight: 600, textAlign: 'right' }}>
                    {t('datatable.sameDayRows.popover.column.stock')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => (
                  <tr key={row.id}>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', whiteSpace: 'nowrap' }}>
                      {formatRowTime(row.date)}
                    </td>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right' }}>
                      {formatCellText(row.weight)}
                    </td>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right' }}>
                      {formatCellText(row.dailyMortality)}
                    </td>
                    <td style={{ padding: '8px 4px', borderBottom: '1px solid #f1f5f9', color: '#0f172a', textAlign: 'right' }}>
                      {formatCellText(row.fishStock)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default SameDayRowsPopover
